import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { motion, useMotionValue, useSpring } from 'motion/react'
import { cn } from '@/lib/utils'

type Variant = 'hidden' | 'default' | 'link' | 'label'

const INTERACTIVE = 'a, button, [role="button"], label, summary'
const TEXT_INPUT = 'input:not([type="checkbox"]):not([type="radio"]), textarea'
const FOLLOW = { stiffness: 700, damping: 45, mass: 0.35 }

const SCALE: Record<Variant, number> = {
    hidden: 0,
    default: 0.16,
    link: 0.5,
    label: 1
}

export const Cursor: FC = (): ReactNode => {
    const [enabled, setEnabled] = useState(false)
    const [variant, setVariant] = useState<Variant>('hidden')
    const [label, setLabel] = useState('')
    const [pressed, setPressed] = useState(false)
    const x = useMotionValue(-200)
    const y = useMotionValue(-200)
    const springX = useSpring(x, FOLLOW)
    const springY = useSpring(y, FOLLOW)

    useEffect(() => {
        const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)')
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

        const sync = (): void => setEnabled(finePointer.matches && !reducedMotion.matches)

        sync()
        finePointer.addEventListener('change', sync)
        reducedMotion.addEventListener('change', sync)

        return () => {
            finePointer.removeEventListener('change', sync)
            reducedMotion.removeEventListener('change', sync)
        }
    }, [])

    useEffect(() => {
        if (!enabled) return

        const root = document.documentElement

        root.classList.add('has-custom-cursor')

        const handleMove = (event: PointerEvent): void => {
            if (event.pointerType !== 'mouse') return

            x.set(event.clientX)
            y.set(event.clientY)

            const target = event.target instanceof Element ? event.target : null

            if (!target) return

            if (target.closest(TEXT_INPUT)) {
                setVariant('hidden')

                return
            }

            const labelled = target.closest<HTMLElement>('[data-cursor]')

            if (labelled) {
                setLabel(labelled.dataset.cursor ?? '')
                setVariant('label')

                return
            }

            setVariant(target.closest(INTERACTIVE) ? 'link' : 'default')
        }

        const handleOut = (event: MouseEvent): void => {
            if (!event.relatedTarget) setVariant('hidden')
        }

        const handleDown = (): void => setPressed(true)
        const handleUp = (): void => setPressed(false)

        window.addEventListener('pointermove', handleMove, { passive: true })
        window.addEventListener('pointerdown', handleDown)
        window.addEventListener('pointerup', handleUp)
        document.addEventListener('mouseout', handleOut)

        return () => {
            root.classList.remove('has-custom-cursor')
            window.removeEventListener('pointermove', handleMove)
            window.removeEventListener('pointerdown', handleDown)
            window.removeEventListener('pointerup', handleUp)
            document.removeEventListener('mouseout', handleOut)
        }
    }, [enabled, x, y])

    if (!enabled) return null

    const scale = SCALE[variant] * (pressed ? 0.82 : 1)
    const isLabel = variant === 'label'

    return (
        <motion.div
            aria-hidden='true'
            className={cn('pointer-events-none fixed left-0 top-0 z-[100]', !isLabel && 'mix-blend-difference')}
            style={{ x: springX, y: springY }}
        >
            <motion.div
                className={cn(
                    '-ml-11 -mt-11 flex size-22 items-center justify-center rounded-full',
                    isLabel ? 'bg-lime text-ink' : 'bg-white'
                )}
                initial={false}
                animate={{ scale }}
                transition={{ type: 'spring', stiffness: 380, damping: 28, mass: 0.6 }}
            >
                <span className={cn('eyebrow font-semibold transition-opacity duration-200', isLabel ? 'opacity-100' : 'opacity-0')}>
                    {label}
                </span>
            </motion.div>
        </motion.div>
    )
}
